<i18n src="./resources/locales.json"></i18n>

<template>
  <section class="conversation-messages-container">
    <div ref="conversationScroller" class="scroller custom-scrollbar">
      <div class="inset-fade-effect" :dark="$vuetify.theme.dark" />
      <div v-if="element.receptor || messages.length">
        <EcsConversationMessage
          v-for="(message, index) in conversation.messages || messages"
          :key="`msg-${index}`"
          :message="message"
          :message-index="index"
          :messages="conversation.messages || messages"
          :receptor="element.receptor"
          :is-sms="isSms"
        />
      </div>
      <EcsTypingAnimationAsMessage
        v-if="typingAnimation && !isSubmitDisabled"
        :typing-participants="conversation.isTyping"
      />
    </div>
    <div class="message-input-container">
      <EcsSubmitInput
        ref="messageInput"
        class="message-input"
        :disabled="isSubmitDisabled"
        :placeholder="inputPlaceHolder"
        @onFocus="handleFocus"
        @onChange="handleIsTyping"
        @onBlur="clearIsTypingNow"
        @onSubmit="sendMessage"
      >
        <template #append>
          <CannedResponses v-if="elementId" :element-id="elementId" @canned-response="handleCannedResponse" />
        </template>
      </EcsSubmitInput>
    </div>
  </section>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash/debounce';
import { mapActions, mapGetters } from 'vuex';
import { IDLE_TIME_BEFORE_NOT_TYPING_REQUEST } from '@/constants/conversation/constants';
import EcsConversationMessage from '@/components/ecs-conversation/ecs-conversation-message/EcsConversationMessage';
import EcsSubmitInput from '@/components/ecs-submit-input/EcsSubmitInput';
import EcsTypingAnimationAsMessage from '@/components/ecs-typing-animation/EcsTypingAnimationAsMessage';
import CannedResponses from '@/components/ecs-interaction/CannedResponses';
import InteractionService from '@/services/interactionService';

export default {
  name: 'EcsConversationMessages',

  components: {
    EcsConversationMessage,
    EcsSubmitInput,
    EcsTypingAnimationAsMessage,
    CannedResponses
  },

  props: {
    elementId: {
      type: String,
      default: ''
    },
    isSms: {
      type: Boolean,
      required: true
    },
    // Only used for historical views.
    messages: {
      type: Array,
      default() {
        return [];
      }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      blockTypingRequests: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONVERSATIONS', ['getConversationForElement']),

    ...mapGetters('ELEMENTS', ['getElement']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    ...mapGetters('WINDOWS', ['focusedInteractionId']),

    conversation() {
      return this.getConversationForElement({ elementId: this.elementId }) || {};
    },

    element() {
      return this.getElement(this.elementId) || {};
    },

    inputPlaceHolder() {
      if (!this.element || !this.element.receptors || this.disabled) {
        return '';
      }

      if (this.element.receptors.length > 1) {
        const receptors = this.element.receptors.map(receptor => receptor.name.split(' ')[0]);
        const commaSeparatedNames = receptors.slice(0, -1).join(', ');
        const lastParticipantName = receptors[receptors.length - 1];
        return this.$t('messageMultiple', { commaSeparatedNames, lastParticipantName });
      } else {
        return this.$t('messageOne', { name: this.element.receptors.length ? this.element.receptors[0].name : '' });
      }
    },

    typingAnimation() {
      return (this.conversation.isTyping || []).length;
    },

    isSubmitDisabled() {
      return (
        this.disabled ||
        this.element.isClosed ||
        this.element.isAbandoned ||
        this.element.isOnHold ||
        this.element.isActivating
      );
    }
  },

  watch: {
    conversation() {
      this.scrollDown();
    }
  },

  mounted() {
    if (this.focusedInteractionId === this.element.interactionId) {
      this.$refs.messageInput.focus();
    }
  },

  methods: {
    ...mapActions('CONVERSATIONS', ['addMessageToConversation']),

    ...mapActions('NOTIFICATIONS', ['clearUnreadMessagesNotifications']),

    /**
     * Scrolls conversation container to the bottom.
     **/
    async scrollDown() {
      await this.$nextTick();
      this.dynamicScrollDown();
    },

    dynamicScrollDown() {
      const container = this.$refs.conversationScroller;
      if (!container) {
        return;
      }

      if (container.scrollTop < container.scrollHeight - container.offsetHeight) {
        container.scrollTop += container.scrollHeight;
        setTimeout(this.dynamicScrollDown, 200);
      }
    },

    sendMessage(message) {
      if (!message) {
        return;
      }

      const completeMessage = {
        elementId: this.elementId,
        originator: {
          id: this.loggedInAgent.id,
          name: this.loggedInAgent.name
        },
        content: message,
        timestamp: moment()
      };

      if (this.isSms) {
        this.sendSmsMessage(message, completeMessage);
      } else {
        this.sendChatMessage(message, completeMessage);
      }
    },

    sendChatMessage(message, completeMessage) {
      InteractionService.sendChatMessage(this.conversation.interactionId, this.conversation.elementId, message).then(
        () => {
          this.addMessageToConversation(completeMessage);
        }
      );
    },

    sendSmsMessage(message, completeMessage) {
      InteractionService.sendSmsMessage(this.conversation.interactionId, this.conversation.elementId, message).then(
        () => {
          this.addMessageToConversation(completeMessage);
        }
      );
    },

    handleCannedResponse(message, elementId) {
      if (this.elementId !== elementId) {
        return;
      }
      if (!this.$refs.messageInput) {
        return;
      }
      if (this.element.isClosed || this.element.isAbandoned || this.element.isOnHold) {
        return;
      }
      this.$refs.messageInput.setMessage(message.text);
    },

    handleIsTyping(inProgressMessage) {
      if (this.isSms) {
        return;
      }
      if (inProgressMessage === '') {
        this.clearIsTypingNow();
      } else {
        this.sendIsTyping();
        this.clearIsTypingDebounced();
      }
      this.clearUnreadMessagesNotifications(this.elementId);
    },

    handleFocus() {
      this.clearUnreadMessagesNotifications(this.elementId);
    },

    sendIsTyping() {
      if (this.blockTypingRequests) {
        return;
      }
      this.blockTypingRequests = true;
      InteractionService.sendChatTypingIndication(this.conversation.interactionId, this.conversation.elementId);
    },

    clearIsTypingNow() {
      if (this.isSms) {
        return;
      }
      if (this.blockTypingRequests) {
        InteractionService.sendChatNotTypingIndication(this.conversation.interactionId, this.conversation.elementId);
      }
      this.blockTypingRequests = false;
    },

    clearIsTypingDebounced: debounce(function() {
      this.clearIsTypingNow();
    }, IDLE_TIME_BEFORE_NOT_TYPING_REQUEST)
  }
};
</script>

<style src="./scoped.css" scoped />
